import React, { useState } from 'react';
import { User, Mail, Globe, MessageSquare, Send } from 'lucide-react';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    website: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch('https://formspree.io/f/mwvwvjbk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', website: '', message: '' });
        setTimeout(() => setSubmitted(false), 4000);
      }
    } catch {
      // silent fail
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="kapcsolat" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center space-y-4 mb-12">
          <span className="text-xs font-bold tracking-[0.2em] text-blue-600 uppercase">
            Kapcsolat
          </span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
            Készen áll a növekedésre?
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            Emelje új szintre webshopja eladásait a piac leggyorsabb AI-alapú ajánlómotorjával.
          </p>
        </div>

        {/* Form card */}
        <div className="max-w-lg mx-auto">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Név */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-900">Név</label>
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Kovács János"
                    className="w-full pl-11 pr-4 py-3 text-sm border border-gray-200 rounded-xl bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    required
                  />
                </div>
              </div>

              {/* Email */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-900">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="janos@webshop.hu"
                    className="w-full pl-11 pr-4 py-3 text-sm border border-gray-200 rounded-xl bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    required
                  />
                </div>
              </div>

              {/* Weboldalad linkje */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-900">Weboldalad linkje</label>
                <div className="relative">
                  <Globe className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="https://webshop.hu"
                    className="w-full pl-11 pr-4 py-3 text-sm border border-gray-200 rounded-xl bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  />
                </div>
              </div>

              {/* Üzenet */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-900">Üzenet</label>
                <div className="relative">
                  <MessageSquare className="absolute left-3.5 top-4 w-4 h-4 text-gray-400" />
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Írd le, miben segíthetünk..."
                    rows={4}
                    className="w-full pl-11 pr-4 py-3 text-sm border border-gray-200 rounded-xl bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none"
                  />
                </div>
              </div>

              {/* Checkbox */}
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer flex-shrink-0"
                />
                <span className="text-xs text-gray-500 leading-relaxed">
                  Elolvastam és elfogadom az{' '}
                  <button
                    type="button"
                    onClick={() => document.getElementById('footer-aszf-btn')?.click()}
                    className="text-blue-600 hover:underline font-medium"
                  >
                    ÁSZF
                  </button>
                  -et és az{' '}
                  <button
                    type="button"
                    onClick={() => document.getElementById('footer-privacy-btn')?.click()}
                    className="text-blue-600 hover:underline font-medium"
                  >
                    Adatkezelési Tájékoztatót
                  </button>
                  .
                </span>
              </label>

              {/* Submit */}
              <button
                type="submit"
                disabled={!accepted || submitting}
                className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-all duration-200 hover:shadow-lg hover:shadow-blue-600/25 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-blue-600 disabled:hover:shadow-none"
              >
                <Send className="w-4 h-4" />
                {submitting ? 'Küldés...' : 'Küldés'}
              </button>

              {/* Success message */}
              {submitted && (
                <div className="text-center py-2 animate-fade-in">
                  <p className="text-green-600 text-sm font-semibold">Köszönjük! Hamarosan jelentkezünk.</p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
